import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  Member,
  MemberHousehold,
  Membership,
  MembershipAddon,
  MembershipPaymentMethod,
  MembershipType,
  PaginatedResponse,
} from '@court-sync/shared';
import { TierFeatureKey, memberCreateSchema, resolveDisplayedMembershipEndDate } from '@court-sync/shared';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout/PageHeader';
import { DashboardTabNav } from '@/components/dashboard/DashboardTabNav';
import {
  DashboardDataCard,
  DashboardFilterCard,
} from '@/components/dashboard/DashboardSectionCard';
import {
  DashboardEmptyStateCard,
  DashboardEmptyStateIcon,
} from '@/components/dashboard/DashboardEmptyState';
import { ClubFeatureGate } from '@/components/club/ClubFeatureGate';
import { ClubMembershipSettingsTab } from '@/components/club/ClubMembershipSettingsTab';
import { MembershipTypeDrawer } from '@/components/club/MembershipTypeDrawer';
import { MembershipAddonDrawer } from '@/components/club/MembershipAddonDrawer';
import { MemberDrawer } from '@/components/club/MemberDrawer';
import { AssignMembershipDrawer, MembershipStripeConfirm } from '@/components/club/AssignMembershipDrawer';
import { SendClubNoticeModal } from '@/components/club/SendClubNoticeModal';
import { ClubMembersTable } from '@/components/club/ClubMembersTable';
import { ClubNoticeHistoryDrawer } from '@/components/club/ClubNoticeHistoryDrawer';
import { ImportModal, type ImportFailure, type ImportResult, type ImportRow } from '@/components/ImportModal';
import {
  Badge,
  Button,
  Drawer,
  Field,
  FieldLabel,
  Input,
  Modal,
  PaginatedTable,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  type PaginatedTableColumn,
  type PaginationState,
} from '@/components/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import {
  dashboardTableCellClass,
  dashboardTableDataRowClass,
  dashboardTableHeadClass,
  dashboardTableHeaderRowClass,
} from '@/components/dashboard/DashboardSectionCard';
import { useCurrentClub } from '@/hooks/useCurrentClub';
import { useClubOwnerAccess } from '@/hooks/useClubOwnerAccess';
import { clubMembersService, type MembershipPurchaseResponse } from '@/services/clubMembers';
import { downloadCSV } from '@/utils/csvUtils';
import { getCurrencyFromRegion, formatCurrency, type Currency } from '@/utils/currency';
import {
  Banknote,
  CalendarClock,
  ChevronDown,
  Download,
  IdCard,
  Loader2,
  MoreVertical,
  Pencil,
  Plus,
  Search,
  ShieldCheck,
  Sparkles,
  Upload,
  Users,
  X,
} from 'lucide-react';
import { useTour } from '@/components/ui/Tour';
import { PageFeatureHelp } from '@/components/tour/PageFeatureHelp';
import { usePageTourActions } from '@/components/tour/PageTourHost';
import { useAutoPageTour } from '@/hooks/useAutoPageTour';
import { CLUB_PAGE_TOUR_STEP_IDS } from '@/lib/tour-constants';
import {
  CLUB_MEMBERS_PAGE_HELP,
  CLUB_MEMBERS_TOUR_KEY,
  getClubMembersTabHelp,
  scheduleClubMembersTourStart,
  type ClubMembersTourTab,
} from '@/lib/tours/clubMembersTour';
import { isPageTourComplete } from '@/lib/tours/pageTourCompletion';
import { scheduleAutoPageTour } from '@/lib/tours/scheduleAutoPageTour';
import { usePageTourReady } from '@/store/pageTourReady';
import { useAuthStore } from '@/store/auth';

type MembersTab = 'members' | 'memberships' | 'types' | 'addons' | 'settings';

const MEMBERS_PAGE_SIZE = 20;
const MEMBERSHIPS_PAGE_SIZE = 20;

function isPaginatedMembersResponse(
  res: { success: boolean; data?: Member[]; pagination?: PaginationState }
): res is PaginatedResponse<Member> {
  return res.success && res.pagination != null;
}

function isPaginatedMembershipsResponse(
  res: { success: boolean; data?: Membership[]; pagination?: PaginationState }
): res is PaginatedResponse<Membership> {
  return res.success && res.pagination != null;
}

const MEMBERS_TABS: {
  id: MembersTab;
  label: string;
  mobileLabel: string;
  tabId?: string;
}[] = [
  {
    id: 'members',
    label: 'Members',
    mobileLabel: 'Members',
    tabId: CLUB_PAGE_TOUR_STEP_IDS.MEMBERS_TAB_MEMBERS,
  },
  {
    id: 'memberships',
    label: 'Memberships',
    mobileLabel: 'Memberships',
    tabId: CLUB_PAGE_TOUR_STEP_IDS.MEMBERS_TAB_MEMBERSHIPS,
  },
  {
    id: 'types',
    label: 'Membership plans',
    mobileLabel: 'Plans',
    tabId: CLUB_PAGE_TOUR_STEP_IDS.MEMBERS_TAB_TYPES,
  },
  {
    id: 'addons',
    label: 'Add-ons',
    mobileLabel: 'Add-ons',
    tabId: CLUB_PAGE_TOUR_STEP_IDS.MEMBERS_TAB_ADDONS,
  },
  {
    id: 'settings',
    label: 'Settings',
    mobileLabel: 'Settings',
    tabId: CLUB_PAGE_TOUR_STEP_IDS.MEMBERS_TAB_SETTINGS,
  },
];

function parseMembersTab(value: string | null): MembersTab {
  if (value === 'memberships') return 'memberships';
  if (value === 'types') return 'types';
  if (value === 'addons') return 'addons';
  if (value === 'settings') return 'settings';
  return 'members';
}

function describeMembershipTerm(type: MembershipType): string {
  if (type.termType === 'recurring') {
    return type.billingInterval === 'year' ? 'Yearly (recurring)' : 'Monthly (recurring)';
  }
  if (type.periodAnchor === 'join_date') {
    return `${type.periodMonths ?? '?'} month${type.periodMonths === 1 ? '' : 's'} from join`;
  }
  return 'Membership year';
}

function formatMembershipPeriodDate(value: Date | string | null | undefined): string {
  return value ? new Date(value).toLocaleDateString() : '—';
}

function formatMembershipPeriodEnds(membership: Membership): string {
  const endDate = resolveDisplayedMembershipEndDate(membership);
  return endDate ? formatMembershipPeriodDate(endDate) : 'Ongoing';
}

function describeMembershipSeats(type: MembershipType): string {
  const parts: string[] = [];
  if (type.maxAdults > 0) {
    parts.push(`${type.maxAdults} adult${type.maxAdults === 1 ? '' : 's'}`);
  }
  if (type.includedJuniors > 0 || type.extraJuniorsFree) {
    const juniorLabel = type.extraJuniorsFree
      ? `${type.includedJuniors}+ junior${type.includedJuniors === 1 ? '' : 's'}`
      : `${type.includedJuniors} junior${type.includedJuniors === 1 ? '' : 's'}`;
    parts.push(juniorLabel);
  }
  return parts.length > 0 ? parts.join(' + ') : '—';
}

const MEMBERSHIP_STATUS_LABELS: Record<Membership['status'], string> = {
  pending_payment: 'Pending payment',
  pending_verification: 'Pending verification',
  active: 'Active',
  past_due: 'Past due',
  expired: 'Expired',
  cancelled: 'Cancelled',
};

const MEMBERSHIP_STATUS_BADGE_VARIANT: Record<Membership['status'], 'success' | 'warning' | 'secondary' | 'destructive'> = {
  pending_payment: 'secondary',
  pending_verification: 'warning',
  active: 'success',
  past_due: 'warning',
  expired: 'secondary',
  cancelled: 'destructive',
};

const MEMBERSHIP_PAYMENT_STATUS_LABELS: Record<Membership['paymentStatus'], string> = {
  paid: 'Paid',
  unpaid: 'Unpaid',
  refunded: 'Refunded',
};

const MEMBERSHIP_PAYMENT_STATUS_BADGE_VARIANT: Record<
  Membership['paymentStatus'],
  'success' | 'warning' | 'secondary'
> = {
  paid: 'success',
  unpaid: 'warning',
  refunded: 'secondary',
};

function formatCentsAsMoney(cents: number, currency: string): string {
  return formatCurrency(cents / 100, currency as Currency);
}

const OFFLINE_PAYMENT_METHODS: { value: Exclude<MembershipPaymentMethod, 'stripe'>; label: string }[] = [
  { value: 'cash', label: 'Cash' },
  { value: 'eftpos', label: 'EFTPOS' },
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'other', label: 'Other' },
];

export default function ClubMembers() {
  const { currentClub, setCurrentClub } = useCurrentClub();
  const { user } = useAuthStore();
  const { startPageTour, startTourWithCompletionKey } = usePageTourActions();
  const { isActive: isTourActive } = useTour();
  // Membership currency and surcharge eligibility resolve from the club's own market, not the
  // signed-in operator's — an org_owner or platform admin may manage clubs in a different region.
  const currency = getCurrencyFromRegion(currentClub?.region ?? undefined);
  const { canManageClubPayments } = useClubOwnerAccess();

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseMembersTab(searchParams.get('tab'));
  const setActiveTabRef = useRef<(tab: MembersTab) => void>(() => {});
  const memberIdFromUrl = searchParams.get('member');
  const openedMemberFromUrlRef = useRef<string | null>(null);

  const [memberRows, setMemberRows] = useState<Member[]>([]);
  const [memberPagination, setMemberPagination] = useState<PaginationState | null>(null);
  const [memberLookup, setMemberLookup] = useState<Member[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [memberPage, setMemberPage] = useState(1);
  const [memberSearchInput, setMemberSearchInput] = useState('');
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [households, setHouseholds] = useState<MemberHousehold[]>([]);
  const [types, setTypes] = useState<MembershipType[]>([]);
  const [addons, setAddons] = useState<MembershipAddon[]>([]);
  const [membershipRows, setMembershipRows] = useState<Membership[]>([]);
  const [membershipPagination, setMembershipPagination] = useState<PaginationState | null>(null);
  const [membershipsLoading, setMembershipsLoading] = useState(false);
  const [membershipPage, setMembershipPage] = useState(1);
  const [membershipSearchInput, setMembershipSearchInput] = useState('');
  const [membershipSearchQuery, setMembershipSearchQuery] = useState('');
  const [pendingVerificationList, setPendingVerificationList] = useState<Membership[]>([]);
  const [renewalsDue, setRenewalsDue] = useState<Membership[]>([]);
  const [loading, setLoading] = useState(true);

  const [typeDrawerOpen, setTypeDrawerOpen] = useState(false);
  const [editingType, setEditingType] = useState<MembershipType | null>(null);
  const typeCreateTourScheduledRef = useRef(false);
  const cancelTypeCreateTourScheduleRef = useRef<(() => void) | null>(null);
  const [deactivateModalType, setDeactivateModalType] = useState<MembershipType | null>(null);
  const [deactivating, setDeactivating] = useState(false);
  const [addonDrawerOpen, setAddonDrawerOpen] = useState(false);
  const [editingAddon, setEditingAddon] = useState<MembershipAddon | null>(null);
  const [noticeModalOpen, setNoticeModalOpen] = useState(false);
  const [showNoticeHistory, setShowNoticeHistory] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(new Set());
  const [deactivateModalAddon, setDeactivateModalAddon] = useState<MembershipAddon | null>(null);
  const [deactivatingAddon, setDeactivatingAddon] = useState(false);

  const [memberDrawerOpen, setMemberDrawerOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [deactivateModalMember, setDeactivateModalMember] = useState<Member | null>(null);
  const [deactivatingMember, setDeactivatingMember] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [exportingMembers, setExportingMembers] = useState(false);

  const [assignDrawerOpen, setAssignDrawerOpen] = useState(false);
  const [membershipStatusFilter, setMembershipStatusFilter] = useState<Membership['status'] | 'all'>('all');

  const [recordPaymentModal, setRecordPaymentModal] = useState<Membership | null>(null);
  const [recordPaymentMethod, setRecordPaymentMethod] = useState<Exclude<MembershipPaymentMethod, 'stripe'>>('cash');
  const [recordPaymentNote, setRecordPaymentNote] = useState('');
  const [recordingPayment, setRecordingPayment] = useState(false);

  const [rejectModal, setRejectModal] = useState<Membership | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [approvingId, setApprovingId] = useState<string | null>(null);

  const [cancelModal, setCancelModal] = useState<Membership | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelRefund, setCancelRefund] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const [renewingId, setRenewingId] = useState<string | null>(null);
  const [renewPurchase, setRenewPurchase] = useState<MembershipPurchaseResponse | null>(null);

  const setActiveTab = useCallback(
    (tab: MembersTab) => {
      const next = new URLSearchParams(searchParams);
      if (tab === 'members') {
        next.delete('tab');
      } else {
        next.set('tab', tab);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    setActiveTabRef.current = setActiveTab;
  }, [setActiveTab]);

  const loadData = useCallback(async () => {
    if (!currentClub) return;
    setLoading(true);
    try {
      const [householdsRes, typesRes, addonsRes] = await Promise.all([
        clubMembersService.listHouseholds(currentClub.id),
        clubMembersService.listMembershipTypes(currentClub.id),
        clubMembersService.listMembershipAddons(currentClub.id),
      ]);
      setHouseholds(householdsRes.success && householdsRes.data ? householdsRes.data : []);
      setTypes(typesRes.success && typesRes.data ? typesRes.data : []);
      setAddons(addonsRes.success && addonsRes.data ? addonsRes.data : []);
    } finally {
      setLoading(false);
    }
  }, [currentClub]);

  const loadRenewalsDue = useCallback(async () => {
    if (!currentClub) return;
    const renewalsRes = await clubMembersService.listRenewalsDue(currentClub.id);
    setRenewalsDue(renewalsRes.success && renewalsRes.data ? renewalsRes.data : []);
  }, [currentClub]);

  const loadPendingVerification = useCallback(async () => {
    if (!currentClub) return;
    const res = await clubMembersService.listMemberships(currentClub.id, {
      status: 'pending_verification',
    });
    setPendingVerificationList(res.success && res.data ? res.data : []);
  }, [currentClub]);

  const loadMembershipsPage = useCallback(
    async (page: number) => {
      if (!currentClub) return;
      setMembershipsLoading(true);
      try {
        const res = await clubMembersService.listMemberships(currentClub.id, {
          page,
          limit: MEMBERSHIPS_PAGE_SIZE,
          search: membershipSearchQuery || undefined,
          ...(membershipStatusFilter !== 'all' ? { status: membershipStatusFilter } : {}),
        });
        if (isPaginatedMembershipsResponse(res)) {
          setMembershipRows(res.data ?? []);
          setMembershipPagination(res.pagination);
        } else if (res.success && res.data) {
          setMembershipRows(res.data);
          setMembershipPagination({
            page: 1,
            limit: res.data.length,
            total: res.data.length,
            totalPages: 1,
          });
        } else {
          setMembershipRows([]);
          setMembershipPagination(null);
        }
      } finally {
        setMembershipsLoading(false);
      }
    },
    [currentClub, membershipSearchQuery, membershipStatusFilter]
  );

  const loadMemberLookup = useCallback(async () => {
    if (!currentClub) return;
    const res = await clubMembersService.listMembers(currentClub.id);
    if (res.success && res.data) {
      setMemberLookup(res.data);
    }
  }, [currentClub]);

  const loadMembersPage = useCallback(
    async (page: number) => {
      if (!currentClub) return;
      setMembersLoading(true);
      try {
        const res = await clubMembersService.listMembers(currentClub.id, {
          page,
          limit: MEMBERS_PAGE_SIZE,
          search: memberSearchQuery || undefined,
        });
        if (isPaginatedMembersResponse(res)) {
          setMemberRows(res.data ?? []);
          setMemberPagination(res.pagination);
        } else if (res.success && res.data) {
          setMemberRows(res.data);
          setMemberPagination({
            page: 1,
            limit: res.data.length,
            total: res.data.length,
            totalPages: 1,
          });
        } else {
          setMemberRows([]);
          setMemberPagination(null);
        }
      } finally {
        setMembersLoading(false);
      }
    },
    [currentClub, memberSearchQuery]
  );

  useEffect(() => {
    void loadData();
    void loadRenewalsDue();
  }, [loadData, loadRenewalsDue]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const trimmed = memberSearchInput.trim();
      setMemberSearchQuery((prev) => (prev === trimmed ? prev : trimmed));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [memberSearchInput]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const trimmed = membershipSearchInput.trim();
      setMembershipSearchQuery((prev) => (prev === trimmed ? prev : trimmed));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [membershipSearchInput]);

  useEffect(() => {
    setMemberPage(1);
  }, [memberSearchQuery]);

  useEffect(() => {
    setMembershipPage(1);
  }, [membershipSearchQuery, membershipStatusFilter]);

  useEffect(() => {
    if (activeTab !== 'members') return;
    void loadMembersPage(memberPage);
  }, [activeTab, loadMembersPage, memberPage]);

  useEffect(() => {
    if (activeTab !== 'memberships') return;
    void loadMembershipsPage(membershipPage);
    void loadPendingVerification();
  }, [activeTab, loadMembershipsPage, loadPendingVerification, membershipPage]);

  useEffect(() => {
    if (activeTab === 'memberships' || assignDrawerOpen) {
      void loadMemberLookup();
    }
  }, [activeTab, assignDrawerOpen, loadMemberLookup]);

  const refreshMembersTab = useCallback(async () => {
    await Promise.all([loadMembersPage(memberPage), loadMemberLookup()]);
  }, [loadMemberLookup, loadMembersPage, memberPage]);

  const refreshMembershipsTab = useCallback(async () => {
    await Promise.all([
      loadMembershipsPage(membershipPage),
      loadPendingVerification(),
      loadRenewalsDue(),
      loadMemberLookup(),
    ]);
  }, [loadMemberLookup, loadMembershipsPage, loadPendingVerification, loadRenewalsDue, membershipPage]);

  const memberById = useMemo(() => new Map(memberLookup.map((m) => [m.id, m])), [memberLookup]);
  const householdById = useMemo(() => new Map(households.map((h) => [h.id, h])), [households]);
  const typeById = useMemo(() => new Map(types.map((t) => [t.id, t])), [types]);

  const householdLabel = useCallback(
    (householdId: string | null): string => {
      if (!householdId) return '—';
      const household = householdById.get(householdId);
      return household?.name?.trim() || 'Household';
    },
    [householdById]
  );

  const memberTotalCount = memberPagination?.total ?? memberRows.length;
  const membershipTotalCount = membershipPagination?.total ?? membershipRows.length;
  const membershipsExist =
    membershipTotalCount > 0 || pendingVerificationList.length > 0 || renewalsDue.length > 0;

  const memberName = useCallback(
    (memberId: string): string => {
      const member = memberById.get(memberId);
      return member ? `${member.firstName} ${member.lastName}` : 'Unknown member';
    },
    [memberById]
  );

  const planName = useCallback(
    (membershipTypeId: string): string => typeById.get(membershipTypeId)?.name ?? 'Unknown plan',
    [typeById]
  );

  // ── Membership types ──────────────────────────────────────────────────
  const openCreateType = useCallback(() => {
    setEditingType(null);
    setTypeDrawerOpen(true);
  }, []);

  const openEditType = (type: MembershipType) => {
    setEditingType(type);
    setTypeDrawerOpen(true);
  };

  const closeTypeDrawer = useCallback(() => {
    setTypeDrawerOpen(false);
    setEditingType(null);
  }, []);

  const handleReactivateType = async (type: MembershipType) => {
    if (!currentClub) return;
    const res = await clubMembersService.updateMembershipType(currentClub.id, type.id, {
      isActive: true,
    });
    if (res.success) {
      toast.success(`${type.name} reactivated`);
      await loadData();
    } else {
      toast.error(res.error || 'Failed to reactivate membership type');
    }
  };

  const handleConfirmDeactivate = async () => {
    if (!currentClub || !deactivateModalType) return;
    setDeactivating(true);
    try {
      const res = await clubMembersService.deactivateMembershipType(
        currentClub.id,
        deactivateModalType.id
      );
      if (res.success) {
        toast.success(`${deactivateModalType.name} deactivated`);
        setDeactivateModalType(null);
        await refreshMembershipsTab();
      } else {
        toast.error(res.error || 'Failed to deactivate membership type');
      }
    } finally {
      setDeactivating(false);
    }
  };

  // ── Add-ons ────────────────────────────────────────────────────────────
  const openCreateAddon = () => {
    setEditingAddon(null);
    setAddonDrawerOpen(true);
  };

  const openEditAddon = (addon: MembershipAddon) => {
    setEditingAddon(addon);
    setAddonDrawerOpen(true);
  };

  const closeAddonDrawer = () => {
    setAddonDrawerOpen(false);
    setEditingAddon(null);
  };

  const handleReactivateAddon = async (addon: MembershipAddon) => {
    if (!currentClub) return;
    const res = await clubMembersService.updateMembershipAddon(currentClub.id, addon.id, {
      isActive: true,
    });
    if (res.success) {
      toast.success(`${addon.name} reactivated`);
      await loadData();
    } else {
      toast.error(res.error || 'Failed to reactivate add-on');
    }
  };

  const handleConfirmDeactivateAddon = async () => {
    if (!currentClub || !deactivateModalAddon) return;
    setDeactivatingAddon(true);
    try {
      const res = await clubMembersService.deactivateMembershipAddon(
        currentClub.id,
        deactivateModalAddon.id
      );
      if (res.success) {
        toast.success(`${deactivateModalAddon.name} deactivated`);
        setDeactivateModalAddon(null);
        await refreshMembershipsTab();
      } else {
        toast.error(res.error || 'Failed to deactivate add-on');
      }
    } finally {
      setDeactivatingAddon(false);
    }
  };

  // ── Members ────────────────────────────────────────────────────────────
  const openCreateMember = () => {
    setEditingMember(null);
    setMemberDrawerOpen(true);
  };

  const openEditMember = (member: Member) => {
    setEditingMember(member);
    setMemberDrawerOpen(true);
  };

  const closeMemberDrawer = () => {
    setMemberDrawerOpen(false);
    setEditingMember(null);
  };

  useEffect(() => {
    if (!currentClub || !memberIdFromUrl) {
      openedMemberFromUrlRef.current = null;
      return;
    }
    if (openedMemberFromUrlRef.current === memberIdFromUrl) return;

    const clearMemberParam = () => {
      const next = new URLSearchParams(searchParams);
      next.delete('member');
      setSearchParams(next, { replace: true });
    };

    const openFromUrl = (member: Member) => {
      openedMemberFromUrlRef.current = memberIdFromUrl;
      setEditingMember(member);
      setMemberDrawerOpen(true);
      clearMemberParam();
    };

    if (activeTab !== 'members') {
      setActiveTab('members');
    }

    const fromLookup =
      memberLookup.find((member) => member.id === memberIdFromUrl) ??
      memberRows.find((member) => member.id === memberIdFromUrl);
    if (fromLookup) {
      openFromUrl(fromLookup);
      return;
    }

    void (async () => {
      const res = await clubMembersService.getMember(currentClub.id, memberIdFromUrl);
      if (res.success && res.data) {
        openFromUrl(res.data);
      }
    })();
  }, [
    activeTab,
    currentClub,
    memberIdFromUrl,
    memberLookup,
    memberRows,
    searchParams,
    setSearchParams,
  ]);

  const handleReactivateMember = useCallback(async (member: Member) => {
    if (!currentClub) return;
    const res = await clubMembersService.updateMember(currentClub.id, member.id, { isActive: true });
    if (res.success) {
      toast.success(`${member.firstName} ${member.lastName} restored to the roster`);
      await refreshMembersTab();
    } else {
        toast.error(res.error || 'Failed to restore member');
    }
  }, [currentClub, refreshMembersTab]);

  const handleConfirmDeactivateMember = async () => {
    if (!currentClub || !deactivateModalMember) return;
    setDeactivatingMember(true);
    try {
      const res = await clubMembersService.deactivateMember(currentClub.id, deactivateModalMember.id);
      if (res.success) {
        toast.success(`${deactivateModalMember.firstName} ${deactivateModalMember.lastName} archived`);
        setDeactivateModalMember(null);
        await refreshMembersTab();
      } else {
        toast.error(res.error || 'Failed to archive member');
      }
    } finally {
      setDeactivatingMember(false);
    }
  };

  const handleExportMembers = useCallback(async () => {
    if (!currentClub) return;
    setExportingMembers(true);
    try {
      const res = await clubMembersService.listMembers(currentClub.id);
      const members = res.success && res.data ? res.data : [];
      const exportData = members.map((member) => ({
        firstName: member.firstName,
        lastName: member.lastName,
        email: member.email ?? '',
        phone: member.phone ?? '',
        dateOfBirth: member.dateOfBirth
          ? new Date(member.dateOfBirth).toISOString().slice(0, 10)
          : '',
        gender: member.gender ?? '',
        isActive: member.isActive,
      }));
      const slug = currentClub.name.replace(/\s+/g, '-').toLowerCase();
      downloadCSV(
        `members-${slug}-${new Date().toISOString().slice(0, 10)}.csv`,
        exportData
      );
      toast.success(`Exported ${members.length} member${members.length === 1 ? '' : 's'}`);
    } catch {
      toast.error('Failed to export members');
    } finally {
      setExportingMembers(false);
    }
  }, [currentClub]);

  const handleImportMembers = useCallback(
    async (rows: ImportRow[]): Promise<ImportResult | undefined> => {
      if (!currentClub) return undefined;

      let createdCount = 0;
      let failedCount = 0;
      const failures: ImportFailure[] = [];

      for (const row of rows) {
        if (!row.isValid) {
          failedCount++;
          failures.push({
            rowNumber: row.rowNumber,
            error: row.error ?? 'Invalid row',
            label: `${row.data.firstName ?? ''} ${row.data.lastName ?? ''}`.trim() || undefined,
          });
          continue;
        }

        const parsed = memberCreateSchema.safeParse({
          firstName: row.data.firstName.trim(),
          lastName: row.data.lastName.trim(),
          email: row.data.email?.trim() ? row.data.email.trim() : null,
          phone: row.data.phone?.trim() ? row.data.phone.trim() : undefined,
          dateOfBirth: row.data.dateOfBirth?.trim() ? row.data.dateOfBirth.trim() : null,
          gender: row.data.gender.trim().toLowerCase(),
          isActive: row.data.isActive?.trim().toLowerCase() === 'false' ? false : true,
        });

        if (!parsed.success) {
          failedCount++;
          failures.push({
            rowNumber: row.rowNumber,
            error: parsed.error.errors[0]?.message ?? 'Validation failed',
            label: `${row.data.firstName} ${row.data.lastName}`.trim(),
          });
          continue;
        }

        const res = await clubMembersService.createMember(currentClub.id, parsed.data);
        if (res.success) {
          createdCount++;
        } else {
          failedCount++;
          failures.push({
            rowNumber: row.rowNumber,
            error: res.error ?? 'Failed to create member',
            label: `${parsed.data.firstName} ${parsed.data.lastName}`.trim(),
          });
        }
      }

      if (createdCount > 0) {
        await refreshMembersTab();
      }

      toast.success(
        `Created ${createdCount} member${createdCount === 1 ? '' : 's'}${
          failedCount > 0 ? `, ${failedCount} failed` : ''
        }`
      );
      return { created: createdCount, failed: failedCount, failures };
    },
    [currentClub, refreshMembersTab]
  );

  const membersFormatGuide = (
    <div className="space-y-3 text-xs">
      <div>
        <p className="mb-2 font-semibold">Required columns:</p>
        <ul className="list-inside list-disc space-y-1 text-gray-700 dark:text-gray-300">
          <li>
            <code className="rounded bg-gray-200 px-1 dark:bg-gray-700">firstName</code> — Member first name
          </li>
          <li>
            <code className="rounded bg-gray-200 px-1 dark:bg-gray-700">lastName</code> — Member last name
          </li>
          <li>
            <code className="rounded bg-gray-200 px-1 dark:bg-gray-700">gender</code> —{' '}
            <code className="rounded bg-gray-200 px-1 dark:bg-gray-700">male</code>,{' '}
            <code className="rounded bg-gray-200 px-1 dark:bg-gray-700">female</code>, or{' '}
            <code className="rounded bg-gray-200 px-1 dark:bg-gray-700">other</code>
          </li>
        </ul>
      </div>
      <div>
        <p className="mb-2 font-semibold">Optional columns:</p>
        <ul className="list-inside list-disc space-y-1 text-gray-700 dark:text-gray-300">
          <li>
            <code className="rounded bg-gray-200 px-1 dark:bg-gray-700">email</code> — Contact email
          </li>
          <li>
            <code className="rounded bg-gray-200 px-1 dark:bg-gray-700">phone</code> — E.164 phone (e.g. +61412345678)
          </li>
          <li>
            <code className="rounded bg-gray-200 px-1 dark:bg-gray-700">dateOfBirth</code> — YYYY-MM-DD
          </li>
          <li>
            <code className="rounded bg-gray-200 px-1 dark:bg-gray-700">isActive</code> — &quot;true&quot; or
            &quot;false&quot; (defaults to true)
          </li>
        </ul>
      </div>
      <div className="rounded bg-blue-100 p-2 text-gray-700 dark:bg-blue-900/30 dark:text-gray-300">
        <strong>Example:</strong> firstName,lastName,gender,email,phone,dateOfBirth,isActive
        <br />
        Alex,Smith,male,alex@example.com,+61412345678,1990-01-15,true
      </div>
    </div>
  );

  const toggleMemberSelect = useCallback((id: string) => {
    setSelectedMemberIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAllMembers = useCallback(() => {
    if (memberRows.every((m) => selectedMemberIds.has(m.id))) {
      setSelectedMemberIds(new Set());
    } else {
      setSelectedMemberIds(new Set(memberRows.map((m) => m.id)));
    }
  }, [memberRows, selectedMemberIds]);

  const clearMemberSelection = useCallback(() => setSelectedMemberIds(new Set()), []);

  const openRecordPayment = (membership: Membership) => {
    setRecordPaymentModal(membership);
    setRecordPaymentMethod('cash');
    setRecordPaymentNote('');
  };

  const handleConfirmRecordPayment = async () => {
    if (!currentClub || !recordPaymentModal) return;
    setRecordingPayment(true);
    try {
      const res = await clubMembersService.recordOfflinePayment(currentClub.id, {
        membershipId: recordPaymentModal.id,
        paymentMethod: recordPaymentMethod,
        note: recordPaymentNote.trim() || undefined,
      });
      if (res.success) {
        toast.success('Payment recorded');
        setRecordPaymentModal(null);
        await refreshMembershipsTab();
      } else {
        toast.error(res.error || 'Failed to record payment');
      }
    } finally {
      setRecordingPayment(false);
    }
  };

  const handleApprove = async (membership: Membership) => {
    if (!currentClub) return;
    setApprovingId(membership.id);
    try {
      const res = await clubMembersService.verifyMembershipProof(currentClub.id, {
        membershipId: membership.id,
        approve: true,
      });
      if (res.success) {
        toast.success('Membership verified');
        await refreshMembershipsTab();
      } else {
        toast.error(res.error || 'Failed to verify membership');
      }
    } finally {
      setApprovingId(null);
    }
  };

  const openReject = (membership: Membership) => {
    setRejectModal(membership);
    setRejectNote('');
  };

  const handleConfirmReject = async () => {
    if (!currentClub || !rejectModal) return;
    setRejecting(true);
    try {
      const res = await clubMembersService.verifyMembershipProof(currentClub.id, {
        membershipId: rejectModal.id,
        approve: false,
        note: rejectNote.trim() || undefined,
      });
      if (res.success) {
        toast.success('Membership rejected');
        setRejectModal(null);
        await refreshMembershipsTab();
      } else {
        toast.error(res.error || 'Failed to reject membership');
      }
    } finally {
      setRejecting(false);
    }
  };

  const openCancel = (membership: Membership) => {
    setCancelModal(membership);
    setCancelReason('');
    setCancelRefund(false);
  };

  const handleConfirmCancel = async () => {
    if (!currentClub || !cancelModal) return;
    setCancelling(true);
    try {
      const res = await clubMembersService.cancelMembership(currentClub.id, {
        membershipId: cancelModal.id,
        reason: cancelReason.trim() || undefined,
        refund: cancelRefund,
      });
      if (res.success) {
        toast.success('Membership cancelled');
        setCancelModal(null);
        await refreshMembershipsTab();
      } else {
        toast.error(res.error || 'Failed to cancel membership');
      }
    } finally {
      setCancelling(false);
    }
  };

  const handleRenew = async (membership: Membership) => {
    if (!currentClub) return;
    setRenewingId(membership.id);
    try {
      const res = await clubMembersService.renewMembership(currentClub.id, { membershipId: membership.id });
      if (res.success && res.data) {
        if (res.data.clientSecret) {
          setRenewPurchase(res.data);
        } else {
          toast.success('Membership renewed');
          await refreshMembershipsTab();
        }
      } else {
        toast.error(res.error || 'Failed to renew membership');
      }
    } finally {
      setRenewingId(null);
    }
  };

  const closeRenewPurchase = () => {
    setRenewPurchase(null);
  };

  const handleRenewPaymentSuccess = async () => {
    toast.success('Renewal payment collected');
    setRenewPurchase(null);
    await refreshMembershipsTab();
  };

  const renderMembershipActions = useCallback(
    (membership: Membership) => {
      if (!canManageClubPayments) return null;

      if (membership.status === 'pending_payment' || membership.status === 'past_due') {
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Actions for ${memberName(membership.primaryMemberId)}`}
              >
                <MoreVertical className="h-4 w-4" aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => openRecordPayment(membership)}>
                <Banknote className="h-4 w-4" aria-hidden />
                Record offline payment
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => openCancel(membership)}>Cancel</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      }

      if (membership.status === 'active') {
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Actions for ${memberName(membership.primaryMemberId)}`}
              >
                <MoreVertical className="h-4 w-4" aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => openCancel(membership)}>Cancel</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      }

      return null;
    },
    [canManageClubPayments, memberName, openCancel, openRecordPayment]
  );

  const membershipTableColumns = useMemo<PaginatedTableColumn<Membership>[]>(
    () => [
      {
        key: 'member',
        header: 'Member',
        render: (membership) => (
          <p className="font-medium text-gray-900 dark:text-white">
            {memberName(membership.primaryMemberId)}
          </p>
        ),
      },
      {
        key: 'plan',
        header: 'Plan',
        render: (membership) => planName(membership.membershipTypeId),
      },
      {
        key: 'status',
        header: 'Status',
        render: (membership) => (
          <Badge variant={MEMBERSHIP_STATUS_BADGE_VARIANT[membership.status]}>
            {MEMBERSHIP_STATUS_LABELS[membership.status]}
          </Badge>
        ),
      },
      {
        key: 'payment',
        header: 'Payment',
        render: (membership) => (
          <Badge variant={MEMBERSHIP_PAYMENT_STATUS_BADGE_VARIANT[membership.paymentStatus]}>
            {MEMBERSHIP_PAYMENT_STATUS_LABELS[membership.paymentStatus]}
          </Badge>
        ),
      },
      {
        key: 'total',
        header: 'Total',
        render: (membership) => formatCentsAsMoney(membership.totalCents, membership.currency),
      },
      {
        key: 'periodStarts',
        header: 'Period starts',
        render: (membership) => formatMembershipPeriodDate(membership.startDate),
      },
      {
        key: 'periodEnds',
        header: 'Period ends',
        render: (membership) => formatMembershipPeriodEnds(membership),
      },
      {
        key: 'actions',
        header: <span className="sr-only">Actions</span>,
        className: 'text-right',
        render: (membership) => renderMembershipActions(membership),
      },
    ],
    [memberName, planName, renderMembershipActions]
  );

  const launchMembersTour = useCallback(() => {
    return scheduleClubMembersTourStart(startTourWithCompletionKey, {
      onEnterMembers: () => setActiveTabRef.current('members'),
      onEnterMemberships: () => setActiveTabRef.current('memberships'),
      onEnterTypes: () => setActiveTabRef.current('types'),
      onEnterAddons: () => setActiveTabRef.current('addons'),
      onEnterSettings: () => setActiveTabRef.current('settings'),
      onOpenCreateType: openCreateType,
      onCloseCreateType: closeTypeDrawer,
      onFinish: () => {
        closeTypeDrawer();
        setActiveTabRef.current('members');
      },
    });
  }, [closeTypeDrawer, openCreateType, startTourWithCompletionKey]);

  const isMembersTourReady = Boolean(currentClub) && !membersLoading && !loading;

  useAutoPageTour(CLUB_MEMBERS_TOUR_KEY, isMembersTourReady, {
    onStart: launchMembersTour,
    afterAnimationMs: 0,
  });

  usePageTourReady(
    'club-members-type-create',
    typeDrawerOpen && !editingType && Boolean(currentClub),
  );

  const handleTypeCreateDrawerPanelOpen = useCallback(() => {
    if (editingType || typeCreateTourScheduledRef.current || isTourActive) return;
    if (!isPageTourComplete(user?.preferences, CLUB_MEMBERS_TOUR_KEY)) return;
    if (isPageTourComplete(user?.preferences, 'club-members-type-create')) return;
    typeCreateTourScheduledRef.current = true;
    cancelTypeCreateTourScheduleRef.current?.();
    cancelTypeCreateTourScheduleRef.current = scheduleAutoPageTour(
      startPageTour,
      'club-members-type-create',
      user?.preferences,
    );
  }, [editingType, isTourActive, startPageTour, user?.preferences]);

  useEffect(() => {
    if (!typeDrawerOpen) {
      typeCreateTourScheduledRef.current = false;
      cancelTypeCreateTourScheduleRef.current?.();
      cancelTypeCreateTourScheduleRef.current = null;
    }
  }, [typeDrawerOpen]);

  const membersTourTab: ClubMembersTourTab =
    activeTab === 'types' || activeTab === 'settings' ? activeTab : 'members';

  const membersTabHelp = useMemo(
    () => getClubMembersTabHelp(membersTourTab),
    [membersTourTab],
  );

  const headerAction = (
    <div className="flex flex-wrap items-center gap-2">
      {activeTab === 'members' && memberTotalCount > 0 ? (
        <Button type="button" onClick={openCreateMember}>
          <Plus className="h-4 w-4" aria-hidden />
          Add member
        </Button>
      ) : activeTab === 'memberships' && (memberLookup.length > 0 || membershipsExist) ? (
        <Button type="button" onClick={() => setAssignDrawerOpen(true)}>
          <Plus className="h-4 w-4" aria-hidden />
          Assign membership
        </Button>
      ) : activeTab === 'types' ? (
        <Button
          type="button"
          id={CLUB_PAGE_TOUR_STEP_IDS.MEMBERS_TYPES_NEW}
          onClick={openCreateType}
        >
          <Plus className="h-4 w-4" aria-hidden />
          {types.length === 0 ? 'Create membership plan' : 'New membership plan'}
        </Button>
      ) : activeTab === 'addons' && addons.length > 0 ? (
        <Button type="button" onClick={openCreateAddon}>
          <Plus className="h-4 w-4" aria-hidden />
          New add-on
        </Button>
      ) : null}
    </div>
  );

  const activeMembershipTypes = types.filter((t) => t.isActive);
  const activeAddons = addons.filter((a) => a.isActive);

  return (
    <ClubFeatureGate feature={TierFeatureKey.MEMBERSHIPS}>
      <div className="contents">
        <PageHeader
          title="Members"
          subtitle="Roster and membership plans"
          breadcrumbs={[{ label: 'Members', href: '/admin/members' }]}
          actions={headerAction}
          help={
            <PageFeatureHelp
              title={CLUB_MEMBERS_PAGE_HELP.title}
              tours={[
                {
                  tourKey: CLUB_MEMBERS_TOUR_KEY,
                  label: CLUB_MEMBERS_PAGE_HELP.tourLabel,
                  description: CLUB_MEMBERS_PAGE_HELP.tourDescription,
                  onStart: launchMembersTour,
                },
              ]}
            >
              <p>{CLUB_MEMBERS_PAGE_HELP.description}</p>
              <p className="mt-2 text-muted-foreground">{membersTabHelp.description}</p>
            </PageFeatureHelp>
          }
          helpAnchorId={CLUB_PAGE_TOUR_STEP_IDS.MEMBERS_GUIDE}
        />

        <div id={CLUB_PAGE_TOUR_STEP_IDS.MEMBERS_TABS}>
          <DashboardTabNav<MembersTab>
            tabs={MEMBERS_TABS}
            activeTab={activeTab}
            onChange={setActiveTab}
            ariaLabel="Members sections"
          />
        </div>

        {loading && activeTab === 'types' ? (
          <div
            id={CLUB_PAGE_TOUR_STEP_IDS.MEMBERS_TYPES}
            className="flex min-h-[160px] items-center justify-center"
            aria-busy="true"
          >
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
          </div>
        ) : loading && activeTab !== 'members' && activeTab !== 'memberships' ? (
          <div className="flex min-h-[160px] items-center justify-center" aria-busy="true">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
          </div>
        ) : activeTab === 'members' ? (
          memberTotalCount === 0 && !membersLoading && !memberSearchQuery ? (
            <DashboardEmptyStateCard>
              <DashboardEmptyStateIcon icon={Users} />
              <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">No members yet</h3>
              <p className="mb-4 text-gray-500 dark:text-gray-400">
                Add club members to sell memberships and apply court hire discounts.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button type="button" onClick={openCreateMember}>
                  <Plus className="h-4 w-4" aria-hidden />
                  Add member
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowImportModal(true)}>
                  <Upload className="h-4 w-4" aria-hidden />
                  Import members
                </Button>
              </div>
            </DashboardEmptyStateCard>
          ) : (
            <>
              <div id={CLUB_PAGE_TOUR_STEP_IDS.MEMBERS_ROSTER} className="mb-6 flex flex-col gap-3 sm:gap-4">
                <div className="relative min-w-0 w-full">
                  <Search
                    className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                    aria-hidden
                  />
                  <input
                    type="search"
                    value={memberSearchInput}
                    onChange={(event) => setMemberSearchInput(event.target.value)}
                    placeholder="Search members by name or email…"
                    aria-label="Search members"
                    autoComplete="off"
                    spellCheck={false}
                    className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-10 text-sm text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                  />
                  {memberSearchInput ? (
                    <button
                      type="button"
                      onClick={() => {
                        setMemberSearchInput('');
                        setMemberSearchQuery('');
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                      aria-label="Clear search"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>

                <div className="flex flex-col gap-3 min-[480px]:flex-row min-[480px]:items-center min-[480px]:justify-between">
                  <span className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
                    {memberTotalCount} member{memberTotalCount === 1 ? '' : 's'}
                  </span>
                  <div className="flex shrink-0 items-center justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowNoticeHistory(true)}
                    >
                      Notice history
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          aria-label="Member data actions"
                          disabled={exportingMembers}
                        >
                          <MoreVertical className="h-4 w-4" aria-hidden />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuItem
                          onSelect={() => void handleExportMembers()}
                          disabled={exportingMembers}
                        >
                          {exportingMembers ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                          ) : (
                            <Download className="mr-2 h-4 w-4" aria-hidden />
                          )}
                          {exportingMembers ? 'Exporting…' : 'Export'}
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => setShowImportModal(true)}>
                          <Upload className="mr-2 h-4 w-4" aria-hidden />
                          Import
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>

              <DashboardDataCard
                toolbar={
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {selectedMemberIds.size > 0
                        ? `${selectedMemberIds.size} selected`
                        : 'Select rows to send a notice to members.'}
                    </span>
                    <div className="ml-auto flex flex-wrap items-center gap-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={selectedMemberIds.size === 0}
                            className="flex items-center gap-1.5"
                            aria-label="Bulk actions for selected members"
                          >
                            Bulk actions
                            <ChevronDown className="h-4 w-4 opacity-70" aria-hidden />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="min-w-[12rem]">
                          <DropdownMenuItem onSelect={() => setNoticeModalOpen(true)}>
                            Send notice
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={selectedMemberIds.size === 0}
                        onClick={clearMemberSelection}
                      >
                        Deselect all
                      </Button>
                    </div>
                  </div>
                }
              >
                <ClubMembersTable
                  members={memberRows}
                  pagination={memberPagination}
                  loading={membersLoading}
                  emptyMessage={
                    memberSearchQuery ? 'No members match your search.' : 'No members found.'
                  }
                  onPageChange={setMemberPage}
                  householdLabel={householdLabel}
                  selectedIds={selectedMemberIds}
                  onToggleSelect={toggleMemberSelect}
                  onToggleSelectAll={toggleSelectAllMembers}
                  onEditMember={openEditMember}
                  onArchiveMember={setDeactivateModalMember}
                  onReactivateMember={handleReactivateMember}
                />
              </DashboardDataCard>
            </>
          )
        ) : activeTab === 'memberships' ? (
          <div className="space-y-6">
            {pendingVerificationList.length > 0 ? (
              <DashboardFilterCard
                title="Pending verification"
                description="Online purchases waiting on proof (e.g. concession card) before they become active."
              >
                <DashboardDataCard className="border-0 shadow-none">
                  <Table>
                    <TableHeader>
                      <TableRow className={dashboardTableHeaderRowClass}>
                        <TableHead className={dashboardTableHeadClass}>Member</TableHead>
                        <TableHead className={dashboardTableHeadClass}>Plan</TableHead>
                        <TableHead className={dashboardTableHeadClass}>Total</TableHead>
                        <TableHead className={dashboardTableHeadClass}>
                          <span className="sr-only">Actions</span>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingVerificationList.map((membership) => (
                        <TableRow key={membership.id} className={dashboardTableDataRowClass}>
                          <TableCell className={dashboardTableCellClass}>
                            {memberName(membership.primaryMemberId)}
                          </TableCell>
                          <TableCell className={dashboardTableCellClass}>
                            {planName(membership.membershipTypeId)}
                          </TableCell>
                          <TableCell className={dashboardTableCellClass}>
                            {formatCentsAsMoney(membership.totalCents, membership.currency)}
                          </TableCell>
                          <TableCell className={dashboardTableCellClass}>
                            {canManageClubPayments ? (
                              <div className="flex justify-end gap-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openReject(membership)}
                                >
                                  Reject
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  isLoading={approvingId === membership.id}
                                  onClick={() => void handleApprove(membership)}
                                >
                                  Approve
                                </Button>
                              </div>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </DashboardDataCard>
              </DashboardFilterCard>
            ) : null}

            {renewalsDue.length > 0 ? (
              <DashboardFilterCard
                title="Renewals due"
                description="Fixed-period memberships ending soon."
              >
                <DashboardDataCard className="border-0 shadow-none">
                  <Table>
                    <TableHeader>
                      <TableRow className={dashboardTableHeaderRowClass}>
                        <TableHead className={dashboardTableHeadClass}>Member</TableHead>
                        <TableHead className={dashboardTableHeadClass}>Plan</TableHead>
                        <TableHead className={dashboardTableHeadClass}>Ends</TableHead>
                        <TableHead className={dashboardTableHeadClass}>
                          <span className="sr-only">Actions</span>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {renewalsDue.map((membership) => (
                        <TableRow key={membership.id} className={dashboardTableDataRowClass}>
                          <TableCell className={dashboardTableCellClass}>
                            {memberName(membership.primaryMemberId)}
                          </TableCell>
                          <TableCell className={dashboardTableCellClass}>
                            {planName(membership.membershipTypeId)}
                          </TableCell>
                          <TableCell className={dashboardTableCellClass}>
                            {membership.endDate ? formatMembershipPeriodDate(membership.endDate) : '—'}
                          </TableCell>
                          <TableCell className={dashboardTableCellClass}>
                            {canManageClubPayments ? (
                              <div className="flex justify-end">
                                <Button
                                  type="button"
                                  size="sm"
                                  isLoading={renewingId === membership.id}
                                  onClick={() => void handleRenew(membership)}
                                >
                                  <CalendarClock className="h-4 w-4" aria-hidden />
                                  Renew
                                </Button>
                              </div>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </DashboardDataCard>
              </DashboardFilterCard>
            ) : null}

            {!membershipsExist && !membershipsLoading && !membershipSearchQuery && membershipStatusFilter === 'all' ? (
              <DashboardEmptyStateCard>
                <DashboardEmptyStateIcon icon={ShieldCheck} />
                <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">No memberships yet</h3>
                <p className="mb-4 text-gray-500 dark:text-gray-400">
                  Assign a membership plan to a member to get started.
                </p>
                <Button type="button" onClick={() => setAssignDrawerOpen(true)}>
                  <Plus className="h-4 w-4" aria-hidden />
                  Assign membership
                </Button>
              </DashboardEmptyStateCard>
            ) : (
              <DashboardDataCard
                toolbar={
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <p className="text-sm text-muted-foreground">
                      {membershipTotalCount} membership{membershipTotalCount === 1 ? '' : 's'}
                    </p>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                      <div className="relative w-full sm:max-w-xs">
                        <Search
                          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                          aria-hidden
                        />
                        <Input
                          type="search"
                          value={membershipSearchInput}
                          onChange={(event) => setMembershipSearchInput(event.target.value)}
                          placeholder="Search by member"
                          className="pl-9"
                          aria-label="Search memberships"
                        />
                      </div>
                      <Select
                        value={membershipStatusFilter}
                        onValueChange={(v) =>
                          setMembershipStatusFilter(v as Membership['status'] | 'all')
                        }
                      >
                        <SelectTrigger className="w-full sm:w-56">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All statuses</SelectItem>
                          {Object.entries(MEMBERSHIP_STATUS_LABELS).map(([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                }
              >
                <PaginatedTable<Membership>
                  columns={membershipTableColumns}
                  data={membershipRows}
                  pagination={membershipPagination}
                  loading={membershipsLoading}
                  emptyMessage={
                    membershipSearchQuery || membershipStatusFilter !== 'all'
                      ? 'No memberships match your filters.'
                      : 'No memberships found.'
                  }
                  onPageChange={setMembershipPage}
                  noWrapperPadding
                />
              </DashboardDataCard>
            )}
          </div>
        ) : activeTab === 'types' ? (
          types.length === 0 ? (
            <DashboardEmptyStateCard>
              <DashboardEmptyStateIcon icon={IdCard} />
              <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">No membership plans</h3>
              <p className="mb-4 text-gray-500 dark:text-gray-400">
                Create membership plans with pricing and court hire benefits.
              </p>
              <Button type="button" onClick={openCreateType}>
                <Plus className="h-4 w-4" aria-hidden />
                Create membership plan
              </Button>
            </DashboardEmptyStateCard>
          ) : (
            <DashboardDataCard>
              <div id={CLUB_PAGE_TOUR_STEP_IDS.MEMBERS_TYPES}>
              <Table>
                <TableHeader>
                  <TableRow className={dashboardTableHeaderRowClass}>
                    <TableHead className={dashboardTableHeadClass}>Name</TableHead>
                    <TableHead className={dashboardTableHeadClass}>Price</TableHead>
                    <TableHead className={dashboardTableHeadClass}>Term</TableHead>
                    <TableHead className={dashboardTableHeadClass}>Seats</TableHead>
                    <TableHead className={dashboardTableHeadClass}>Public</TableHead>
                    <TableHead className={dashboardTableHeadClass}>Active</TableHead>
                    <TableHead className={dashboardTableHeadClass}>
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {types.map((type) => (
                    <TableRow key={type.id} className={dashboardTableDataRowClass}>
                      <TableCell className={dashboardTableCellClass}>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{type.name}</p>
                          {type.description ? (
                            <p className="text-sm text-gray-500 dark:text-gray-400">{type.description}</p>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className={dashboardTableCellClass}>
                        {(type.priceCents / 100).toFixed(2)} {type.currency}
                      </TableCell>
                      <TableCell className={dashboardTableCellClass}>
                        {describeMembershipTerm(type)}
                      </TableCell>
                      <TableCell className={dashboardTableCellClass}>
                        {describeMembershipSeats(type)}
                      </TableCell>
                      <TableCell className={dashboardTableCellClass}>
                        {type.isPublic ? 'Yes' : 'No'}
                      </TableCell>
                      <TableCell className={dashboardTableCellClass}>
                        {type.isActive ? 'Yes' : 'No'}
                      </TableCell>
                      <TableCell className={dashboardTableCellClass}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              aria-label={`Actions for ${type.name}`}
                            >
                              <MoreVertical className="h-4 w-4" aria-hidden />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={() => openEditType(type)}>
                              <Pencil className="h-4 w-4" aria-hidden />
                              Edit
                            </DropdownMenuItem>
                            {type.isActive ? (
                              <DropdownMenuItem onSelect={() => setDeactivateModalType(type)}>
                                Deactivate
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onSelect={() => void handleReactivateType(type)}>
                                Reactivate
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            </DashboardDataCard>
          )
        ) : activeTab === 'settings' ? (
          currentClub ? (
            <div id={CLUB_PAGE_TOUR_STEP_IDS.MEMBERS_SETTINGS}>
              <ClubMembershipSettingsTab
                club={currentClub}
                onSaved={(updated) => setCurrentClub(updated)}
              />
            </div>
          ) : null
        ) : activeTab === 'addons' ? (
          addons.length === 0 ? (
            <DashboardEmptyStateCard>
              <DashboardEmptyStateIcon icon={Sparkles} />
              <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">No add-ons yet</h3>
              <p className="mb-4 text-gray-500 dark:text-gray-400">
                Create stackable per-adult or per-membership extras members can add to a membership,
                e.g. Gold Extra or Night Extra.
              </p>
              <Button type="button" onClick={openCreateAddon}>
                <Plus className="h-4 w-4" aria-hidden />
                Create add-on
              </Button>
            </DashboardEmptyStateCard>
          ) : (
            <DashboardDataCard>
              <Table>
                <TableHeader>
                  <TableRow className={dashboardTableHeaderRowClass}>
                    <TableHead className={dashboardTableHeadClass}>Name</TableHead>
                    <TableHead className={dashboardTableHeadClass}>Price</TableHead>
                    <TableHead className={dashboardTableHeadClass}>Concession price</TableHead>
                    <TableHead className={dashboardTableHeadClass}>Pricing</TableHead>
                    <TableHead className={dashboardTableHeadClass}>Active</TableHead>
                    <TableHead className={dashboardTableHeadClass}>
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {addons.map((addon) => (
                    <TableRow key={addon.id} className={dashboardTableDataRowClass}>
                      <TableCell className={dashboardTableCellClass}>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{addon.name}</p>
                          {addon.description ? (
                            <p className="text-sm text-gray-500 dark:text-gray-400">{addon.description}</p>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className={dashboardTableCellClass}>
                        {(addon.priceCents / 100).toFixed(2)} {addon.currency}
                      </TableCell>
                      <TableCell className={dashboardTableCellClass}>
                        {addon.concessionPriceCents != null
                          ? `${(addon.concessionPriceCents / 100).toFixed(2)} ${addon.currency}`
                          : '—'}
                      </TableCell>
                      <TableCell className={dashboardTableCellClass}>
                        {addon.pricingMode === 'per_adult' ? 'Per adult' : 'Per membership'}
                      </TableCell>
                      <TableCell className={dashboardTableCellClass}>
                        {addon.isActive ? 'Yes' : 'No'}
                      </TableCell>
                      <TableCell className={dashboardTableCellClass}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              aria-label={`Actions for ${addon.name}`}
                            >
                              <MoreVertical className="h-4 w-4" aria-hidden />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={() => openEditAddon(addon)}>
                              <Pencil className="h-4 w-4" aria-hidden />
                              Edit
                            </DropdownMenuItem>
                            {addon.isActive ? (
                              <DropdownMenuItem onSelect={() => setDeactivateModalAddon(addon)}>
                                Deactivate
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onSelect={() => void handleReactivateAddon(addon)}>
                                Reactivate
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </DashboardDataCard>
          )
        ) : null}

        {currentClub ? (
          <MembershipTypeDrawer
            clubId={currentClub.id}
            currency={currency}
            membershipType={editingType}
            isOpen={typeDrawerOpen}
            onClose={closeTypeDrawer}
            onCreatePanelOpen={
              !editingType && !isTourActive ? handleTypeCreateDrawerPanelOpen : undefined
            }
            showCreateTourHelp={!editingType && !isTourActive}
            showBackdrop={!isTourActive}
            onSaved={loadData}
          />
        ) : null}

        {currentClub ? (
          <MembershipAddonDrawer
            clubId={currentClub.id}
            currency={currency}
            addon={editingAddon}
            isOpen={addonDrawerOpen}
            onClose={closeAddonDrawer}
            onSaved={loadData}
          />
        ) : null}

        {currentClub ? (
          <MemberDrawer
            clubId={currentClub.id}
            member={editingMember}
            households={households}
            defaultPhoneCountrySource={{ region: currentClub.region }}
            isOpen={memberDrawerOpen}
            onClose={closeMemberDrawer}
            onSaved={refreshMembersTab}
          />
        ) : null}

        {currentClub ? (
          <AssignMembershipDrawer
            clubId={currentClub.id}
            currency={currency}
            types={activeMembershipTypes}
            addons={activeAddons}
            members={memberLookup.filter((m) => m.isActive)}
            households={households}
            canManageClubPayments={canManageClubPayments}
            isOpen={assignDrawerOpen}
            onClose={() => setAssignDrawerOpen(false)}
            onAssigned={refreshMembershipsTab}
          />
        ) : null}

        <Modal
          isOpen={deactivateModalType != null}
          onClose={() => setDeactivateModalType(null)}
          title="Deactivate membership plan?"
          maxWidth="md"
          stackLevel="elevated"
        >
          <p className="text-sm text-gray-600 dark:text-gray-400">
            New members can no longer be assigned{' '}
            <span className="font-medium text-gray-900 dark:text-white">
              {deactivateModalType?.name}
            </span>
            . Existing subscriptions continue to bill until cancelled.
          </p>
          <div className="mt-6 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setDeactivateModalType(null)}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void handleConfirmDeactivate()} isLoading={deactivating}>
              Deactivate
            </Button>
          </div>
        </Modal>

        <Modal
          isOpen={deactivateModalAddon != null}
          onClose={() => setDeactivateModalAddon(null)}
          title="Deactivate add-on?"
          maxWidth="md"
          stackLevel="elevated"
        >
          <p className="text-sm text-gray-600 dark:text-gray-400">
            New memberships can no longer select{' '}
            <span className="font-medium text-gray-900 dark:text-white">
              {deactivateModalAddon?.name}
            </span>
            . Existing selections are unaffected.
          </p>
          <div className="mt-6 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setDeactivateModalAddon(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handleConfirmDeactivateAddon()}
              isLoading={deactivatingAddon}
            >
              Deactivate
            </Button>
          </div>
        </Modal>

        <Modal
          isOpen={deactivateModalMember != null}
          onClose={() => setDeactivateModalMember(null)}
          title="Archive member?"
          maxWidth="md"
          stackLevel="elevated"
        >
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium text-gray-900 dark:text-white">
              {deactivateModalMember?.firstName} {deactivateModalMember?.lastName}
            </span>{' '}
            will stay on the list but can no longer be selected for new memberships. Existing
            memberships are unaffected.
          </p>
          <div className="mt-6 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setDeactivateModalMember(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handleConfirmDeactivateMember()}
              isLoading={deactivatingMember}
            >
              Archive
            </Button>
          </div>
        </Modal>

        <Modal
          isOpen={recordPaymentModal != null}
          onClose={() => setRecordPaymentModal(null)}
          title="Record offline payment"
          maxWidth="md"
          stackLevel="elevated"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Marks this membership as paid via the method below. Amount:{' '}
              {recordPaymentModal
                ? formatCentsAsMoney(recordPaymentModal.totalCents, recordPaymentModal.currency)
                : ''}
            </p>
            <Field>
              <FieldLabel htmlFor="record-payment-method">Payment method</FieldLabel>
              <Select
                value={recordPaymentMethod}
                onValueChange={(v) => setRecordPaymentMethod(v as Exclude<MembershipPaymentMethod, 'stripe'>)}
              >
                <SelectTrigger id="record-payment-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OFFLINE_PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel htmlFor="record-payment-note">Note (optional)</FieldLabel>
              <Textarea
                id="record-payment-note"
                rows={2}
                value={recordPaymentNote}
                onChange={(e) => setRecordPaymentNote(e.target.value)}
              />
            </Field>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setRecordPaymentModal(null)}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void handleConfirmRecordPayment()} isLoading={recordingPayment}>
              Confirm payment received
            </Button>
          </div>
        </Modal>

        <Modal
          isOpen={rejectModal != null}
          onClose={() => setRejectModal(null)}
          title="Reject membership?"
          maxWidth="md"
          stackLevel="elevated"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              This cancels the membership. If it was paid via Stripe, the payment is refunded automatically.
            </p>
            <Field>
              <FieldLabel htmlFor="reject-note">Reason (optional)</FieldLabel>
              <Textarea
                id="reject-note"
                rows={2}
                placeholder="e.g. proof of concession not valid"
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
              />
            </Field>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setRejectModal(null)}>
              Cancel
            </Button>
            <Button type="button" variant="outline" onClick={() => void handleConfirmReject()} isLoading={rejecting}>
              Reject
            </Button>
          </div>
        </Modal>

        <Modal
          isOpen={cancelModal != null}
          onClose={() => setCancelModal(null)}
          title="Cancel membership?"
          maxWidth="md"
          stackLevel="elevated"
        >
          <div className="space-y-4">
            <Field>
              <FieldLabel htmlFor="cancel-reason">Reason (optional)</FieldLabel>
              <Textarea
                id="cancel-reason"
                rows={2}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
            </Field>
            {cancelModal?.paymentStatus === 'paid' ? (
              <div className="flex items-start gap-3">
                <Switch id="cancel-refund" checked={cancelRefund} onCheckedChange={setCancelRefund} />
                <label htmlFor="cancel-refund" className="cursor-pointer text-sm">
                  <span className="font-medium text-gray-900 dark:text-white">Refund this payment</span>
                  <p className="mt-1 text-gray-600 dark:text-gray-400">
                    Refunds the Stripe payment, or acknowledges an offline refund already given.
                  </p>
                </label>
              </div>
            ) : null}
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setCancelModal(null)}>
              Back
            </Button>
            <Button type="button" onClick={() => void handleConfirmCancel()} isLoading={cancelling}>
              Cancel membership
            </Button>
          </div>
        </Modal>

        <Drawer
          isOpen={renewPurchase != null}
          onClose={closeRenewPurchase}
          title="Complete renewal payment"
          description="Collect the payment for this membership's next term."
          width="md"
        >
          {renewPurchase ? (
            <MembershipStripeConfirm
              purchase={renewPurchase}
              currency={renewPurchase.currency}
              onSuccess={() => void handleRenewPaymentSuccess()}
              onCancel={closeRenewPurchase}
            />
          ) : null}
        </Drawer>

        {currentClub ? (
          <>
            <SendClubNoticeModal
              clubId={currentClub.id}
              open={noticeModalOpen}
              onOpenChange={setNoticeModalOpen}
              selectedMemberIds={[...selectedMemberIds]}
              onSuccess={clearMemberSelection}
            />
            <ClubNoticeHistoryDrawer
              clubId={currentClub.id}
              open={showNoticeHistory}
              onClose={() => setShowNoticeHistory(false)}
            />
          </>
        ) : null}

        <ImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onImport={handleImportMembers}
          title="Import members"
          description="Upload a CSV file to import multiple club members at once"
          formatGuide={membersFormatGuide}
          importingLabel="Importing members…"
          requiredColumns={['firstName', 'lastName', 'gender']}
          additionalRowError={(row) => {
            const gender = row.gender?.trim().toLowerCase();
            if (gender && !['male', 'female', 'other'].includes(gender)) {
              return 'gender must be male, female, or other';
            }
            return undefined;
          }}
        />
      </div>
    </ClubFeatureGate>
  );
}
